import type { ReactNode } from "react";
import type { DoorType, OpeningKind } from "../types/C1_ModelTypes";

/** Visual editor metadata shared by opening placement and object navigation. */
export interface C1_OpeningDefinition {
  id: OpeningKind | "passage";
  description: {
    de: string;
    en: string;
  };
  icon: ReactNode;
}

const windowDefinition: C1_OpeningDefinition = {
  id: "window",
  description: { de: "Fenster", en: "Window" },
  icon: (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="butt" strokeLinejoin="miter">
      <rect x="3" y="2" width="18" height="18" />
      <path d="M12 3.5V18.5" style={{ strokeWidth: 0.3 }} />
      <path d="M4.5 18.5V3.5h15v15z" style={{ strokeWidth: 0.3 }} />
      <path d="M12 14h3" />
      <path d="M2 20h20M2 22h20" />
    </svg>
  ),
};

const doorDefinition: C1_OpeningDefinition = {
  id: "door",
  description: { de: "Türe", en: "Door" },
  icon: (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="butt" strokeLinejoin="miter">
      <path d="M11 20H2M11 4H6v16M22 20h-3" />
      <path d="M11 5.5H7.5v13H11" style={{ strokeWidth: 0.3 }} />
      <g>
        <path d="M11 3v19l8-2V5z" />
        <path d="M14 12h.01" strokeLinecap="round" />
      </g>
    </svg>
  ),
};

const passageDefinition: C1_OpeningDefinition = {
  id: "passage",
  description: { de: "Durchgang", en: "Passage" },
  icon: (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="butt" strokeLinejoin="miter">
      <path d="M2 21h4V3h12v18h4M9 21h6" />
    </svg>
  ),
};

const garageDoorDefinition: C1_OpeningDefinition = {
  id: "garage-door",
  description: { de: "Garagentor", en: "Garage door" },
  icon: (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="butt" strokeLinejoin="miter">
      <path d="M2 21V7l10-5 10 5v14z" />
      <path d="M6 21V9h12v12M6 13h12M6 17h12" />
    </svg>
  ),
};

export const openingDefinitions: readonly C1_OpeningDefinition[] = [windowDefinition, doorDefinition, passageDefinition, garageDoorDefinition];

/** Resolves the visual object definition independently of the opening's ELO profile. */
export function getOpeningDefinition(kind: OpeningKind, doorType?: DoorType): C1_OpeningDefinition {
  if (kind === "door" && doorType === "passage") return passageDefinition;
  if (kind === "window") return windowDefinition;
  if (kind === "garage-door") return garageDoorDefinition;
  return doorDefinition;
}
